import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import './style.scss';
import MenuItem from '@material-ui/core/MenuItem';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import Icon from 'components/Utils/NewIcon';
import { DefaultSwitch } from 'services/UIUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import type {
  FeeOption,
  TenantAdministeredDrug,
  TenantFeeSchedule,
  TenantImmunizationFee,
} from 'generated/api';

/**
 * Tenant "Service Fees" editor:
 * - Immunization Fees: attach labeled fee options to global catalog items
 *   (the catalog itself is managed in the ops portal — tenants only price it).
 * - Administered Drugs: fully tenant-owned entries (name/description/route)
 *   with optional fee options. New tenants start empty.
 *
 * A fee option marked patient-facing is the price a patient may be quoted and
 * charged by default; non-patient-facing options (e.g. staff rates) never
 * surface to patients. Saves are optimistic-concurrency guarded server-side —
 * a 409 means someone else saved first, so we reload.
 */
const ServiceFees = () => {
  const dispatch = useAppDispatch();
  const immunizationDescriptions = useAppSelector((state) => state.immunizationDescriptions);

  const [schedule, setSchedule] = useState<TenantFeeSchedule | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const result = await dispatch(api.HealthRecords.getTenantFeeSchedule(), {
      errorTitle: 'Service Fees',
    });
    if (result) setSchedule(PerformanceUtils.clone(result));
  };

  useEffect(() => {
    load();
    dispatch(api.HealthRecords.getImmunizationDescriptions());
  }, []);

  const update = (changes: Partial<TenantFeeSchedule>) =>
    setSchedule((current) => (current ? { ...current, ...changes } : current));

  async function save() {
    if (!schedule) return;
    setSaving(true);
    const result = await dispatch(
      api.HealthRecords.updateTenantFeeSchedule({ tenantFeeSchedule: schedule }),
      {
        title: 'Service Fees',
        successText: 'Service fees saved!',
      },
    );
    setSaving(false);
    if (result) {
      setSchedule(PerformanceUtils.clone(result));
      return;
    }
    // Failed. Distinguish a version conflict (our base is stale — reload,
    // there is nothing valid to keep) from a validation error (keep the
    // admin's edits so a typo'd label doesn't wipe their work).
    const current = await dispatch(api.HealthRecords.getTenantFeeSchedule());
    if (current && (current.version ?? 0) !== (schedule.version ?? 0)) {
      setSchedule(PerformanceUtils.clone(current));
    }
  }

  if (!schedule) return null;

  const immunizationFees = schedule.immunization_fees ?? [];
  const drugs = schedule.administered_drugs ?? [];

  const activeCatalog = immunizationDescriptions.filter((imm: any) => imm.active !== false);
  const usedItemIds = new Set(immunizationFees.map((entry) => entry.catalog_item_id));

  const saveRow = (
    <div className="row button-wrapper">
      <DefaultButton name="Save" className="blue" onClick={save} loading={saving} />
    </div>
  );

  return (
    <div className="service-fees full-height-panel full-width section-cards-layout">
      <div className="section">
        <span className="small-title">Immunization Fees</span>
        <p className="hint">
          Attach prices to immunizations from the global catalog. Immunizations without an entry can
          still be recorded — they just offer no charge at administration time.
        </p>
        {immunizationFees.map((entry, index) => (
          <div className="fee-entry" key={entry.catalog_item_id || index}>
            <div className="row entry-header">
              <DefaultSelectField
                label="Immunization"
                value={entry.catalog_item_id ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const next = [...immunizationFees];
                  next[index] = { ...entry, catalog_item_id: event.target.value };
                  update({ immunization_fees: next });
                }}
                inputElements={activeCatalog
                  .filter(
                    (imm: any) => imm.id === entry.catalog_item_id || !usedItemIds.has(imm.id),
                  )
                  .map((imm: any) => (
                    <MenuItem value={imm.id} key={imm.id}>
                      {imm.name}
                    </MenuItem>
                  ))}
                style={{ width: '250px' }}
              />
              <RemoveButton
                onClick={() =>
                  update({
                    immunization_fees: immunizationFees.filter((_, i) => i !== index),
                  })
                }
              />
            </div>
            <FeeOptionsEditor
              fees={entry.fees ?? []}
              onChange={(fees) => {
                const next = [...immunizationFees];
                next[index] = { ...entry, fees };
                update({ immunization_fees: next });
              }}
            />
          </div>
        ))}
        <div className="row">
          <DefaultButton
            name="Add Immunization Fee"
            icon="plus"
            className="white add-entry"
            onClick={() =>
              update({
                immunization_fees: [
                  ...immunizationFees,
                  { catalog_item_id: '', fees: [] } as TenantImmunizationFee,
                ],
              })
            }
          />
        </div>
        {saveRow}
      </div>

      <div className="section">
        <span className="small-title">Administered Drugs</span>
        <p className="hint">
          Drugs your clinic administers (injectables, infusions, etc.). These are specific to your
          clinic and appear as prefill options in the Med Admin form. Prices are optional — leave
          them off for drugs that are free or prepaid at booking.
        </p>
        {drugs.map((drug, index) => (
          <div className="fee-entry" key={drug.id || index}>
            <div className="row entry-header">
              <DefaultTextField
                label="Name"
                value={drug.name ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const next = [...drugs];
                  next[index] = { ...drug, name: event.target.value };
                  update({ administered_drugs: next });
                }}
                style={{ width: '200px' }}
              />
              <DefaultTextField
                label="Description"
                value={drug.description ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const next = [...drugs];
                  next[index] = { ...drug, description: event.target.value };
                  update({ administered_drugs: next });
                }}
                style={{ width: '300px' }}
              />
              <DefaultTextField
                label="Route"
                value={drug.route ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const next = [...drugs];
                  next[index] = { ...drug, route: event.target.value };
                  update({ administered_drugs: next });
                }}
                style={{ width: '180px' }}
              />
              <RemoveButton
                onClick={() => update({ administered_drugs: drugs.filter((_, i) => i !== index) })}
              />
            </div>
            <FeeOptionsEditor
              fees={drug.fees ?? []}
              onChange={(fees) => {
                const next = [...drugs];
                next[index] = { ...drug, fees };
                update({ administered_drugs: next });
              }}
            />
          </div>
        ))}
        <div className="row">
          <DefaultButton
            name="Add Administered Drug"
            icon="plus"
            className="white add-entry"
            onClick={() =>
              update({
                administered_drugs: [
                  ...drugs,
                  { name: '', description: '', route: '', fees: [] } as TenantAdministeredDrug,
                ],
              })
            }
          />
        </div>
        {saveRow}
      </div>

      {(schedule.history?.length ?? 0) > 0 && (
        <div className="section history">
          <span className="small-title">Change History</span>
          {schedule.history!.slice(0, 10).map((entry, index) => (
            <div className="history-entry" key={index}>
              <span className="date">
                {entry.date ? moment.unix(entry.date).format('YYYY/MM/DD HH:mm') : ''}
              </span>
              <span className="summary">{entry.summary}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PATIENT_FACING_TOOLTIP =
  'Patient-facing prices can be quoted to patients (including by the AI assistant) and are the ' +
  'default charge. Uncheck for internal rates like staff pricing — those are never shown to ' +
  'patients.';

const FeeOptionsEditor = ({
  fees,
  onChange,
}: {
  fees: FeeOption[];
  onChange: (fees: FeeOption[]) => void;
}) => (
  <div className="fee-options">
    {fees.map((fee, index) => (
      <div className="row fee-option" key={fee.id || index}>
        <DefaultTextField
          label="Fee Label"
          placeholder="e.g. Standard, Staff"
          value={fee.label ?? ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const next = [...fees];
            next[index] = { ...fee, label: event.target.value };
            onChange(next);
          }}
          style={{ width: '220px' }}
        />
        <DefaultTextField
          label="Amount ($)"
          type="number"
          value={String(fee.amount ?? '')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const next = [...fees];
            next[index] = { ...fee, amount: parseFloat(event.target.value) || 0 };
            onChange(next);
          }}
          style={{ width: '120px' }}
        />
        <DefaultSwitch
          name={`patient-facing-${index}`}
          label={
            <span>
              {'Patient-facing '}
              <Icon
                src="circle-info"
                alt={PATIENT_FACING_TOOLTIP}
                showTooltip={true}
                tooltipPlace="top"
                width="13px"
                style={{ display: 'inline-block' }}
              />
            </span>
          }
          padded={false}
          checked={fee.patient_facing === true}
          onChange={(event: unknown, isInputChecked: boolean) => {
            const next = [...fees];
            next[index] = { ...fee, patient_facing: isInputChecked };
            onChange(next);
          }}
        />
        <RemoveButton onClick={() => onChange(fees.filter((_, i) => i !== index))} />
      </div>
    ))}
    <span
      className="add-fee clickable"
      onClick={() => onChange([...fees, { label: '', amount: 0, patient_facing: true }])}
    >
      + Add fee option
    </span>
  </div>
);

const RemoveButton = ({ onClick }: { onClick: () => void }) => (
  <Icon
    src="trash"
    alt="Remove"
    width="16px"
    className="clickable remove-entry"
    onClick={onClick}
  />
);

export default ServiceFees;
